/**
 * Archon Data endpoints: Cost model versions, Rollout schedule imports, Paid
 * invoices and their validation, Installation windows, Origin settings, gold
 * authoring's writes and perspective rerun, and retiring, restoring and making
 * draft quizzes live.
 *
 * Transport, auth and the error envelope come from `forgeRequest`, exactly as
 * in `ArchonApiService`. Wire casing follows ADR-0021: responses are camelCase,
 * query parameters are written snake_case here, and request bodies are handed
 * over as camelCase for `forgeRequest`'s `toWire` to convert.
 */
import { forgeFetch, forgeRequest, forgeVoidRequest } from '../../forge/services/forgeRequest';
import type {
  ArchonGoldArea,
  ArchonGoldBox,
  ArchonGoldDecision,
  ArchonGoldIssue,
  ArchonGoldPoint,
  ArchonGoldSourceKind,
  ArchonGoldStart,
  ArchonGoldTaskState,
  ArchonQuizPoolAction,
} from './ArchonApiService';

const API_DL_URL = process.env.REACT_APP_API_DL_URL;

if (!API_DL_URL) {
  throw new Error('REACT_APP_API_DL_URL is required for Archon API requests');
}

const ARCHON_API_URL = `${API_DL_URL}/v1/forge/archon`;

export type ArchonVendor = 'cogito' | 'oworkers';

export const ARCHON_VENDORS: ArchonVendor[] = ['cogito', 'oworkers'];

export const ARCHON_VENDOR_LABELS: Record<ArchonVendor, string> = {
  cogito: 'Cogito',
  oworkers: 'Oworkers',
};

/* ------------------------------------------------------------------ 1. Cost model */

export interface ArchonRateCard {
  draw: number;
  drawEdit: number;
  pair: number;
  review: number;
}

/** One F1 percentage (0–100) per adjustable operation class; Draw edit is exempt. */
export interface ArchonQualityScores {
  draw: number;
  pair: number;
  review: number;
}

/**
 * Which F1 judges a vendor-month's Quality adjustment: the month's quizzes, or
 * the F1 the vendor billed on while invoices use a fixed F1 (the month's Paid
 * invoice, or the latest earlier one listing F1; the quizzes when none does).
 */
export type ArchonQualityF1Source = 'quizzes' | 'invoice';

/**
 * A vendor's Quality adjustment terms: a class whose month F1 falls short of its
 * target forgoes `multiplierAbove × shortfall` percent of that class's gross
 * dollars, or `multiplierAtOrBelow × shortfall` once the F1 is at or below the
 * floor. Every version answers with them; a version stored before the field
 * existed is read with the contract terms. `f1Source` is optional: a backend
 * that predates it judges on the quizzes.
 */
export interface ArchonQualityTerms {
  targets: ArchonQualityScores;
  floors: ArchonQualityScores;
  multiplierAbove: number;
  multiplierAtOrBelow: number;
  f1Source?: ArchonQualityF1Source;
}

export interface ArchonCostModelVersion {
  id: string;
  effectiveFrom: string;
  rateCards: Record<ArchonVendor, ArchonRateCard>;
  qualityTerms: Record<ArchonVendor, ArchonQualityTerms>;
  vendorSplit: number;
  defaultInstallationDays: number;
  excludedSources: string[];
  calibrationDays: number;
  settleLagDays: number;
  note: string | null;
  createdBy: string;
  createdAt: string;
}

/** A new version: the server assigns id, created_by and created_at. */
export type ArchonCostModelInput = Omit<ArchonCostModelVersion, 'id' | 'createdBy' | 'createdAt'>;

export interface ArchonCostModelsResponse {
  versions: ArchonCostModelVersion[];
}

export function fetchCostModels(token: string): Promise<ArchonCostModelsResponse> {
  return forgeRequest(token, `${ARCHON_API_URL}/cost-models`, 'Error fetching Cost model versions');
}

/** The version applying on `on` (default: today, server side). */
export function fetchActiveCostModel(token: string, on?: string): Promise<ArchonCostModelVersion> {
  const query = new URLSearchParams();
  if (on) query.set('on', on);
  const suffix = query.toString() ? `?${query.toString()}` : '';
  return forgeRequest(
    token,
    `${ARCHON_API_URL}/cost-models/active${suffix}`,
    'Error fetching the active Cost model'
  );
}

export function createCostModel(
  token: string,
  body: ArchonCostModelInput
): Promise<ArchonCostModelVersion> {
  return forgeRequest(token, `${ARCHON_API_URL}/cost-models`, 'Error saving the Cost model', {
    method: 'POST',
    body,
  });
}

/* ----------------------------------------------------- 2. Rollout schedules */

export interface ArchonRolloutStore {
  storeCode: string;
  storeId: number | null;
  name: string;
  plannedDate: string;
  plannedCameras: number;
}

export interface ArchonRolloutImportSummary {
  id: string;
  chainId: number;
  importedAt: string;
  importedBy: string;
  sourceUrl: string | null;
  storeCount: number;
  cameraTotal: number;
  firstPlannedDate: string | null;
  lastPlannedDate: string | null;
}

export interface ArchonRolloutImport {
  id: string;
  chainId: number;
  importedAt: string;
  importedBy: string;
  sourceUrl: string | null;
  stores: ArchonRolloutStore[];
}

export interface ArchonRolloutImportsResponse {
  imports: ArchonRolloutImportSummary[];
}

export interface ArchonRolloutScheduleInput {
  chainId: number;
  sourceUrl: string | null;
  stores: ArchonRolloutStore[];
}

export function fetchRolloutSchedules(
  token: string,
  chainId: number
): Promise<ArchonRolloutImportsResponse> {
  const query = new URLSearchParams({ chain_id: String(chainId) });
  return forgeRequest(
    token,
    `${ARCHON_API_URL}/rollout-schedules?${query.toString()}`,
    'Error fetching Rollout schedule imports'
  );
}

export function fetchRolloutSchedule(token: string, id: string): Promise<ArchonRolloutImport> {
  return forgeRequest(
    token,
    `${ARCHON_API_URL}/rollout-schedules/${encodeURIComponent(id)}`,
    'Error fetching the Rollout schedule import'
  );
}

/**
 * The chain's active (latest) import, or `null` when the chain has never been
 * imported: the endpoint answers 204 for that case.
 */
export async function fetchActiveRolloutSchedule(
  token: string,
  chainId: number
): Promise<ArchonRolloutImport | null> {
  const query = new URLSearchParams({ chain_id: String(chainId) });
  const response = await forgeFetch(
    token,
    `${ARCHON_API_URL}/rollout-schedules/active?${query.toString()}`,
    'Error fetching the active Rollout schedule'
  );
  if (response.status === 204) return null;
  return response.json();
}

export function createRolloutSchedule(
  token: string,
  body: ArchonRolloutScheduleInput
): Promise<ArchonRolloutImport> {
  return forgeRequest(
    token,
    `${ARCHON_API_URL}/rollout-schedules`,
    'Error importing the Rollout schedule',
    { method: 'POST', body }
  );
}

/* --------------------------------------------------------- 3. Paid invoices */

export interface ArchonInvoiceOps {
  draw: number;
  drawEdit: number;
  pair: number;
  review: number;
}

/** The per-class F1 percentages the invoice's Quality adjustment was computed from. */
export interface ArchonInvoiceAccuracy {
  draw: number;
  pair: number;
  review: number;
}

/**
 * One vendor's month as paid. `amountUsd` is the paid total: the operations at
 * their rates, less the Quality adjustment (`qaDeductionUsd`, positive), plus the
 * downtime credit (`downtimeUsd`). `accuracy` and `downtimeUsd` are optional;
 * an invoice recorded before they existed answers with them absent.
 */
export interface ArchonInvoice {
  id: string;
  vendor: ArchonVendor;
  month: string;
  amountUsd: number;
  ops: ArchonInvoiceOps | null;
  qaDeductionUsd: number | null;
  accuracy?: ArchonInvoiceAccuracy | null;
  downtimeUsd?: number | null;
  docRef: string | null;
  updatedBy: string;
  updatedAt: string;
}

export type ArchonInvoiceInput = Omit<ArchonInvoice, 'id' | 'updatedBy' | 'updatedAt'>;

export interface ArchonInvoicesResponse {
  invoices: ArchonInvoice[];
}

/**
 * One operation class of a month's reconciliation: what Archon computed for it
 * against what the vendor invoiced, at that vendor's rate.
 *
 * `invoicedCount` and `invoicedUsd` are null when the invoice carries no
 * operation counts — the amount alone cannot be split across the classes.
 */
export interface ArchonInvoiceValidationClass {
  op: 'draw' | 'draw_edit' | 'pair' | 'review';
  computedCount: number;
  invoicedCount: number | null;
  rateUsd: number;
  computedUsd: number;
  invoicedUsd: number | null;
  /** The computed count opened out by the kind of work it was billed for. */
  byWork: { match: number; templateQa: number; quiz: number };
  /**
   * The class's Quality adjustment: Archon's, from the F1 the row's `f1Source`
   * names, and the invoice's own, recomputed from the F1 it lists with the same
   * terms. Null where there is no F1 to judge on; Draw edit is exempt and
   * reports zero. Optional: a backend that predates the adjustment answers
   * without them.
   */
  computedF1?: number | null;
  computedDiscountUsd?: number;
  invoicedF1?: number | null;
  invoicedDiscountUsd?: number | null;
}

export interface ArchonInvoiceValidationRow {
  month: string;
  vendor: ArchonVendor;
  invoiceAmountUsd: number | null;
  /**
   * The invoice before its Quality adjustment (amount plus deduction, less the
   * downtime credit) and the deduction itself; null when the invoice lists no
   * deduction. Optional on a backend that predates the adjustment.
   */
  invoiceGrossUsd?: number | null;
  invoiceDiscountUsd?: number | null;
  /** The downtime credit the invoice lists; Archon does not price downtime. */
  invoiceDowntimeUsd?: number | null;
  /** This vendor's whole gross billable total; no Vendor split is applied to it. */
  computedUsd: number;
  /**
   * The Quality adjustment Archon models on `computedUsd` from the F1
   * `f1Source` names, and the net it leaves: what Archon expects the vendor to
   * be paid. The residual compares the paid amount against that net.
   */
  computedDiscountUsd?: number;
  computedNetUsd?: number;
  /** Null when no F1 judges the month; absent on a backend that predates it. */
  f1Source?: ArchonQualityF1Source | null;
  computedFromInvoiceOpsUsd: number | null;
  residualPct: number | null;
  /** The residual is over 5%. */
  flagged: boolean;
  /**
   * `computedDiscountUsd` is more than $50 and 10% away from the invoiced
   * deduction. Optional on a backend that predates the check.
   */
  discountFlagged?: boolean;
  dollarShare: number | null;
  /**
   * The per-operation reconciliation. Optional: a backend that predates
   * billable operations answers without it, and the card says so rather than
   * inventing classes.
   */
  classes?: ArchonInvoiceValidationClass[];
}

export interface ArchonInvoiceValidationResponse {
  rows: ArchonInvoiceValidationRow[];
}

export function fetchInvoices(token: string): Promise<ArchonInvoicesResponse> {
  return forgeRequest(token, `${ARCHON_API_URL}/invoices`, 'Error fetching Paid invoices');
}

export function createInvoice(token: string, body: ArchonInvoiceInput): Promise<ArchonInvoice> {
  return forgeRequest(token, `${ARCHON_API_URL}/invoices`, 'Error saving the Paid invoice', {
    method: 'POST',
    body,
  });
}

export function updateInvoice(
  token: string,
  id: string,
  body: ArchonInvoiceInput
): Promise<ArchonInvoice> {
  return forgeRequest(
    token,
    `${ARCHON_API_URL}/invoices/${encodeURIComponent(id)}`,
    'Error saving the Paid invoice',
    { method: 'PUT', body }
  );
}

export function deleteInvoice(token: string, id: string): Promise<void> {
  return forgeVoidRequest(
    token,
    `${ARCHON_API_URL}/invoices/${encodeURIComponent(id)}`,
    'Error deleting the Paid invoice',
    { method: 'DELETE' }
  );
}

export function fetchInvoiceValidation(
  token: string,
  months: number
): Promise<ArchonInvoiceValidationResponse> {
  const query = new URLSearchParams({ months: String(months) });
  return forgeRequest(
    token,
    `${ARCHON_API_URL}/invoices/validation?${query.toString()}`,
    'Error fetching the invoice validation'
  );
}

/* --------------------------------------------------- 4. Installation windows */

export interface ArchonInstallationWindow {
  chainId: number;
  storeId: number;
  firstCameraAt: string;
  signedOffAt: string | null;
  windowEnd: string;
  source: 'signoff' | 'default';
  updatedAt: string;
}

export interface ArchonInstallationWindowsResponse {
  windows: ArchonInstallationWindow[];
}

export function fetchInstallationWindows(
  token: string,
  chainId: number
): Promise<ArchonInstallationWindowsResponse> {
  const query = new URLSearchParams({ chain_id: String(chainId) });
  return forgeRequest(
    token,
    `${ARCHON_API_URL}/installation-windows?${query.toString()}`,
    'Error fetching Installation windows'
  );
}

/* ------------------------------------------------------ 5. Source settings */

/**
 * A fleet-wide override for one origin: how it is named and coloured in
 * every Archon chart, and which source it is folded into. Facts keep the source
 * the pipeline wrote; the mapping is applied at read time.
 */
export interface ArchonSourceSetting {
  /** The raw source key as written by the pipeline. */
  source: string;
  displayName: string | null;
  /** `#rrggbb`, or null to take the next colour from the chart palette. */
  color: string | null;
  /** The source this one is reported under, or null when it stands alone. */
  mergedInto: string | null;
  updatedAt: string;
  updatedBy: string;
}

export interface ArchonSourceSettingsResponse {
  /** The sources that carry an override. */
  sources: ArchonSourceSetting[];
  /** Every source seen in the facts, settled or not. */
  knownSources: string[];
}

/** The writable half of a setting: the server owns updated_by and updated_at. */
export type ArchonSourceSettingInput = Pick<
  ArchonSourceSetting,
  'displayName' | 'color' | 'mergedInto'
>;

export function fetchArchonSourceSettings(token: string): Promise<ArchonSourceSettingsResponse> {
  return forgeRequest(token, `${ARCHON_API_URL}/source-settings`, 'Error fetching Origin settings');
}

/**
 * An empty key would build `/source-settings/`, the collection URL, so the two
 * writers refuse it rather than address the wrong route.
 */
const NO_EMPTY_SOURCE_KEY = 'An Origin setting needs a non-empty origin key.';

export function saveArchonSourceSetting(
  token: string,
  source: string,
  body: ArchonSourceSettingInput
): Promise<ArchonSourceSetting> {
  if (!source) return Promise.reject(new Error(NO_EMPTY_SOURCE_KEY));
  return forgeRequest(
    token,
    `${ARCHON_API_URL}/source-settings/${encodeURIComponent(source)}`,
    'Error saving the Origin setting',
    { method: 'PUT', body }
  );
}

/** Clear the override: the source falls back to its raw key and palette colour. */
export function deleteArchonSourceSetting(token: string, source: string): Promise<void> {
  if (!source) return Promise.reject(new Error(NO_EMPTY_SOURCE_KEY));
  return forgeVoidRequest(
    token,
    `${ARCHON_API_URL}/source-settings/${encodeURIComponent(source)}`,
    'Error clearing the Origin setting',
    { method: 'DELETE' }
  );
}

/* ------------------------------------------------------- 5. QA sampling settings */

export interface ArchonQaSamplingQuota {
  chainId: number;
  /** TemplateQA redraws the sampler may draw for this chain per day. */
  dailyQuota: number;
}

/**
 * One version of the TemplateQA sampling settings. Documents are append-only,
 * newest active, exactly like the Cost model; the legacy sampler job reads the
 * active one nightly.
 */
export interface ArchonQaSampling {
  quotas: ArchonQaSamplingQuota[];
  /** Keep cameras whose ingress is disabled inside the sampling frame. */
  includeIngressDisabled: boolean;
  /** Never hand a redraw to the labeler who drew the live template. */
  excludeTemplateAuthor: boolean;
  /**
   * Fraction of a chain's daily quota drawn from its canary stores, 0 to 1. It
   * only applies to a chain that has canary stores; every other chain draws its
   * whole quota from the general pool. Optional on read: the frontend and
   * api_dl deploy separately, so a version stored before this field existed,
   * or a backend that predates it, returns nothing here.
   */
  canaryShare?: number;
  createdAt: string;
  createdBy: string;
}

/** One chain's sampling frame and its canary part, as of the last facts run. */
export interface ArchonQaSamplingFrameRow {
  asOf: string;
  chainId: number;
  stores: number;
  canaryStores: number;
  liveTemplates: number;
  canaryLiveTemplates: number;
}

export interface ArchonQaSamplingResponse {
  /** null until a first version is written; the sampler then runs on `fallbackQuotas`. */
  active: ArchonQaSampling | null;
  /** Newest first, at most 20. */
  history: ArchonQaSampling[];
  /**
   * Empty until the Archon facts job has written a frame, and absent entirely
   * from a backend deployed before this field existed.
   */
  frame?: ArchonQaSamplingFrameRow[];
  /**
   * The quotas the sampler draws with while `active` is null. Absent from a
   * backend deployed before this field existed.
   */
  fallbackQuotas?: ArchonQaSamplingQuota[];
}

/** The writable half: the server owns created_by and created_at. */
export type ArchonQaSamplingInput = Pick<
  ArchonQaSampling,
  'quotas' | 'includeIngressDisabled' | 'excludeTemplateAuthor'
> & { canaryShare: number };

export function fetchArchonQaSampling(token: string): Promise<ArchonQaSamplingResponse> {
  return forgeRequest(
    token,
    `${ARCHON_API_URL}/qa-sampling`,
    'Error fetching the QA sampling settings'
  );
}

export function saveArchonQaSampling(
  token: string,
  body: ArchonQaSamplingInput
): Promise<ArchonQaSampling> {
  return forgeRequest(
    token,
    `${ARCHON_API_URL}/qa-sampling`,
    'Error saving the QA sampling settings',
    { method: 'PUT', body }
  );
}

/* ------------------------------------------------------------------- 6. Access */

/** What the signed-in user may open in Archon. */
export interface ArchonAccess {
  /** Paid invoices, Cost model and QA sampling are owner-only. */
  canManageData: boolean;
}

export function fetchArchonAccess(token: string): Promise<ArchonAccess> {
  return forgeRequest(token, `${ARCHON_API_URL}/access`, 'Error fetching Archon access');
}

/* --------------------------------------------------------------------- 7. Gold */

/** Gold for a task, drawn in the template of one of its answers or in its own perspective. */
export interface ArchonGoldPublishInput {
  start: ArchonGoldStart;
  frame: ArchonGoldSourceKind;
  decision: ArchonGoldDecision;
  /** Camera-frame points to the gold's: the frame's, or one rerun from the gold's mods. */
  perspectiveMatrix: number[][] | null;
  areas: ArchonGoldArea[];
  boxes: ArchonGoldBox[];
  issues: ArchonGoldIssue[];
  /** Writes the quiz inactive, as a draft the Quiz pool makes live later. */
  draft: boolean;
}

/** A task's state after a publish or skip. */
export interface ArchonGoldItemResponse {
  taskId: number;
  state: ArchonGoldTaskState;
  liveQuizId: number | null;
  draft: boolean;
}

/** The task to work on: the one the caller holds open, else a newly taken one. */
export interface ArchonGoldNextResponse {
  taskId: number;
}

/** Returns the caller's open task, else takes a random recent vendor task nobody took. */
export function nextArchonGoldTask(token: string): Promise<ArchonGoldNextResponse> {
  return forgeRequest(token, `${ARCHON_API_URL}/gold/next`, 'Error taking the next gold task', {
    method: 'POST',
  });
}

/** A task the author chose: a Match task, or the newest on a camera. */
export type ArchonGoldTakeInput = { taskId: number } | { cameraId: number };

/**
 * Takes the chosen task, or the camera's newest, if it makes a quiz and nobody
 * took it; the caller's open task goes back undecided.
 */
export function takeArchonGoldTask(
  token: string,
  body: ArchonGoldTakeInput
): Promise<ArchonGoldNextResponse> {
  return forgeRequest(token, `${ARCHON_API_URL}/gold/take`, 'Error taking the gold task', {
    method: 'POST',
    body,
  });
}

/** Writes the gold as a new quiz, live or a draft. Refused unless the task is the caller's and open. */
export function publishArchonGoldTask(
  token: string,
  taskId: number,
  body: ArchonGoldPublishInput
): Promise<ArchonGoldItemResponse> {
  return forgeRequest(
    token,
    `${ARCHON_API_URL}/gold/tasks/${taskId}/publish`,
    'Error publishing the gold task',
    { method: 'POST', body }
  );
}

/** The task never becomes a quiz and is not offered again. */
export function skipArchonGoldTask(token: string, taskId: number): Promise<ArchonGoldItemResponse> {
  return forgeRequest(
    token,
    `${ARCHON_API_URL}/gold/tasks/${taskId}/skip`,
    'Error skipping the gold task',
    { method: 'POST' }
  );
}

/** Mods in the camera frame of a task's photo: the untransformed photo, of this size. */
export interface ArchonGoldPerspectiveInput {
  width: number;
  height: number;
  mods: ArchonGoldPoint[][];
}

export interface ArchonGoldPerspectiveResponse {
  /** Camera-frame points to the template the mods rectify into. */
  perspectiveMatrix: number[][];
}

/** The perspective the Match tool computes from mods in a photo's camera frame. */
export function rerunArchonGoldPerspective(
  token: string,
  body: ArchonGoldPerspectiveInput
): Promise<ArchonGoldPerspectiveResponse> {
  return forgeRequest(
    token,
    `${ARCHON_API_URL}/gold/perspective`,
    'Error rerunning the perspective',
    { method: 'POST', body }
  );
}

/* ---------------------------------------------------------------- 8. Quiz pool */

/** A recorded retire, restore or publish. */
export interface ArchonQuizPoolChangeResponse {
  action: ArchonQuizPoolAction;
  quizIds: number[];
  authorEmail: string;
  createdAt: string;
}

/**
 * Retires active quizzes, restores quizzes Archon retired, or makes Gold
 * drafts live: legacy stops or starts serving them at once, and Archon records
 * who and when.
 */
export function changeArchonQuizPool(
  token: string,
  action: ArchonQuizPoolAction,
  quizIds: number[],
  reason: string | null = null
): Promise<ArchonQuizPoolChangeResponse> {
  return forgeRequest(
    token,
    `${ARCHON_API_URL}/quiz-pool/${action}`,
    `Error trying to ${action} the quizzes`,
    { method: 'POST', body: { quizIds, reason } }
  );
}
